import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Search,
  UserPlus,
  Trash2,
  Shield,
  X,
  Eye,
  EyeOff,
  Key,
  Plus,
  AlertTriangle,
  Loader2,
} from "lucide-react";

const AVATAR_COLORS = ["#572B7C", "#0F766E", "#2563EB", "#78287C"];

const AdminManagement = () => {
  const navigate = useNavigate();
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) navigate('/szadmin');
    fetchAdminUsers();
  }, [navigate]);

  const fetchAdminUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users`
      );
      setAdminUsers(response.data.admin_users || []);
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password || !formData.full_name) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      const currentAdmin = JSON.parse(localStorage.getItem('shootzu_admin') || '{}');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users`,
        formData,
        {
          params: {
            created_by_email: currentAdmin.email || 'system'
          }
        }
      );

      if (response.data.success) {
        toast.success('Admin user created successfully!');
        setShowAddModal(false);
        setFormData({ email: '', password: '', full_name: '' });
        fetchAdminUsers();
      }
    } catch (error) {
      console.error('Failed to create admin user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create admin user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAdmin = async (adminId, adminEmail) => {
    if (!window.confirm(`Are you sure you want to remove admin user "${adminEmail}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${adminId}`
      );

      if (response.data.success) {
        toast.success('Admin user removed successfully');
        fetchAdminUsers();
      }
    } catch (error) {
      console.error('Failed to delete admin user:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove admin user');
    }
  };

  const handleOpenPasswordModal = (admin) => {
    setSelectedAdmin(admin);
    setPasswordData({ new_password: '', confirm_password: '' });
    setShowPasswordModal(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-admin-password`,
        {
          admin_id: selectedAdmin.id,
          new_password: passwordData.new_password
        }
      );

      if (response.data.success) {
        toast.success('Password reset successfully!');
        setShowPasswordModal(false);
        setPasswordData({ new_password: '', confirm_password: '' });
        setSelectedAdmin(null);
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
  };

  const filteredAdmins = adminUsers.filter(admin =>
    admin.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    admin.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const closeAddModal = () => {
    setShowAddModal(false);
    setFormData({ email: '', password: '', full_name: '' });
    setShowPassword(false);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordData({ new_password: '', confirm_password: '' });
    setSelectedAdmin(null);
    setShowNewPassword(false);
  };

  return (
    <div>
      {/* Sticky Header */}
      <header className="sz-header flex items-center justify-between gap-[18px]">
        <div className="flex items-center gap-[11px]">
          <h1 className="sz-h1 m-0">Admin Users</h1>
          <span
            className="text-[11.5px] font-bold px-[10px] py-1 rounded-full"
            style={{ fontFamily: "Poppins", color: "#78287C", background: "#EDE4F4" }}
          >
            {adminUsers.length} admins
          </span>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="sz-btn sz-btn-primary"
        >
          <Plus className="w-4 h-4" />
          Add Admin
        </button>
      </header>

      {/* Main */}
      <main style={{ padding: "22px 30px 56px", maxWidth: 920 }}>
        {/* Search */}
        <div className="relative max-w-[420px] mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#A8AEBC" }} />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search admins…"
            className="sz-input"
            style={{ paddingLeft: 36 }}
          />
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: "#572B7C" }} />
            <p className="mt-4 text-sm" style={{ color: "#8A90A2" }}>Loading admin users...</p>
          </div>
        ) : filteredAdmins.length === 0 ? (
          <div className="sz-card text-center" style={{ padding: "48px 24px" }}>
            <Shield className="w-14 h-14 mx-auto mb-4" style={{ color: "#A8AEBC" }} />
            <p className="text-base font-semibold mb-1" style={{ color: "#2B3142" }}>No admin users found</p>
            <p className="text-sm mb-6" style={{ color: "#8A90A2" }}>
              {searchQuery ? 'Try adjusting your search' : 'Add your first admin user to get started'}
            </p>
            {!searchQuery && (
              <button onClick={() => setShowAddModal(true)} className="sz-btn sz-btn-primary mx-auto">
                <Plus className="w-4 h-4" />
                Add First Admin
              </button>
            )}
          </div>
        ) : (
          <div
            className="grid gap-[13px]"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}
          >
            {filteredAdmins.map((admin, i) => (
              <div
                key={admin.id}
                className="sz-card sz-fade"
                style={{ padding: 18 }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="relative flex-none flex items-center justify-center text-white"
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 999,
                      background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                      fontFamily: "Poppins",
                      fontWeight: 700,
                      fontSize: 14,
                    }}
                  >
                    {getInitials(admin.full_name)}
                    <span
                      className="absolute flex items-center justify-center"
                      style={{
                        bottom: -2,
                        right: -2,
                        width: 18,
                        height: 18,
                        borderRadius: 999,
                        background: "#78287C",
                        border: "2px solid #fff",
                      }}
                    >
                      <Shield className="w-[10px] h-[10px]" style={{ color: "#fff" }} strokeWidth={3} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-[7px]">
                      <span className="text-sm font-semibold truncate">{admin.full_name}</span>
                      <span
                        className="flex-none font-bold"
                        style={{
                          fontFamily: "Poppins",
                          fontSize: 9.5,
                          color: "#78287C",
                          background: "#EDE4F4",
                          padding: "2px 7px",
                          borderRadius: 5,
                        }}
                      >
                        ADMIN
                      </span>
                    </div>
                    <div
                      className="mt-0.5 truncate"
                      style={{ fontSize: 12, color: "#8A90A2" }}
                    >
                      {admin.email}
                    </div>
                  </div>
                </div>

                <div
                  className="mt-[13px]"
                  style={{ fontSize: 11.5, color: "#8A90A2", lineHeight: 1.5 }}
                >
                  Added {formatDate(admin.created_at)}
                  {admin.created_by && (
                    <>
                      <br />
                      by {admin.created_by}
                    </>
                  )}
                </div>

                <div className="flex gap-2 mt-[14px]">
                  <button
                    onClick={() => handleOpenPasswordModal(admin)}
                    className="flex-1"
                    style={{
                      padding: 8,
                      border: "1px solid #E0E3EB",
                      borderRadius: 9,
                      background: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#2B3142",
                      cursor: "pointer",
                    }}
                  >
                    Reset Password
                  </button>
                  <button
                    onClick={() => handleDeleteAdmin(admin.id, admin.email)}
                    style={{
                      padding: "8px 13px",
                      border: "1px solid #FECACA",
                      borderRadius: 9,
                      background: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#DC2626",
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="sz-modal-backdrop" onClick={closeAddModal}>
          <div
            className="sz-modal-card"
            style={{ width: "100%", maxWidth: 460, overflow: "hidden" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between text-white"
              style={{ padding: "18px 22px", background: "linear-gradient(120deg,#15803D,#0F766E)" }}
            >
              <h3 className="m-0" style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16 }}>
                Add Admin User
              </h3>
              <button
                onClick={closeAddModal}
                className="flex items-center justify-center"
                style={{ width: 30, height: 30, border: "none", borderRadius: 9, background: "rgba(255,255,255,.18)", color: "#fff", cursor: "pointer" }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddAdmin} style={{ padding: "20px 22px" }}>
              <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 600, color: "#2B3142" }}>Full name</label>
              <input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Jane Doe"
                className="sz-input mb-[14px]"
                required
              />

              <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 600, color: "#2B3142" }}>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="jane@shootzu.com"
                className="sz-input mb-[14px]"
                required
              />

              <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 600, color: "#2B3142" }}>Password</label>
              <div className="relative mb-1.5">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Min. 6 characters"
                  className="sz-input"
                  style={{ paddingRight: 40 }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: "#8A90A2" }}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <div
                className="flex items-start gap-2 mt-[10px]"
                style={{ padding: "10px 12px", background: "#FEF3C7", borderRadius: 10, fontSize: 11.5, color: "#92400E", lineHeight: 1.5 }}
              >
                <AlertTriangle className="w-[15px] h-[15px] flex-none mt-px" />
                Admins have full access to all platform data, payments and user records.
              </div>

              <div className="flex gap-[9px] mt-5">
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="flex-1"
                  style={{ padding: 11, border: "1px solid #E0E3EB", borderRadius: 11, background: "#fff", fontSize: 13, fontWeight: 600, color: "#2B3142", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center justify-center gap-2"
                  style={{ flex: 1.4, padding: 11, border: "none", borderRadius: 11, background: "linear-gradient(120deg,#15803D,#0F766E)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1 }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Admin"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedAdmin && (
        <div className="sz-modal-backdrop" onClick={closePasswordModal}>
          <div
            className="sz-modal-card"
            style={{ width: "100%", maxWidth: 460, overflow: "hidden" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between text-white"
              style={{ padding: "18px 22px", background: "linear-gradient(120deg,#1D4ED8,#2563EB)" }}
            >
              <h3 className="m-0" style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16 }}>
                Reset Password
              </h3>
              <button
                onClick={closePasswordModal}
                className="flex items-center justify-center"
                style={{ width: 30, height: 30, border: "none", borderRadius: 9, background: "rgba(255,255,255,.18)", color: "#fff", cursor: "pointer" }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleChangePassword} style={{ padding: "20px 22px" }}>
              <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 600, color: "#2B3142" }}>Email</label>
              <input
                value={selectedAdmin.email}
                readOnly
                disabled
                className="sz-input mb-[14px]"
                style={{ background: "#F5F6F9", color: "#8A90A2" }}
              />

              <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 600, color: "#2B3142" }}>New password</label>
              <div className="relative mb-[14px]">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Min. 6 characters"
                  className="sz-input"
                  style={{ paddingRight: 40 }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: "#8A90A2" }}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 600, color: "#2B3142" }}>Confirm new password</label>
              <input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="Re-enter new password"
                className="sz-input"
                required
              />

              <div className="flex gap-[9px] mt-5">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  className="flex-1"
                  style={{ padding: 11, border: "1px solid #E0E3EB", borderRadius: 11, background: "#fff", fontSize: 13, fontWeight: 600, color: "#2B3142", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center justify-center gap-2"
                  style={{ flex: 1.4, padding: 11, border: "none", borderRadius: 11, background: "linear-gradient(120deg,#1D4ED8,#2563EB)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1 }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
